import { Checkbox, IconButton, InputBase, Box } from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  CheckBoxOutlined as CheckBoxOutlinedIcon,
  Done as DoneIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { css, styled } from '@mui/material/styles';
import { ChangeEvent, MouseEvent, useCallback, useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { QTable, QTableBody, QTableRow, QTableCell, QAsyncTablePagination, QSearchBar, QButton } from '@kw/quokka-ui';
import { sendHttpRequest } from '../../utils/network.service';
import { calculateElapsedTime } from '../../utils/calculateElapsedTime';
import { ResetButton } from '../shared/ResetButton';
import { PageLoader } from '../../PageLoader';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { useFetchService } from '../../utils/fetchService';
import { useStickyResult } from '../../utils/useStickyResult';
import { createQueryString } from '../../utils/createQueryString';
import { useDeviceOwnerSortParams } from './useDeviceOwnersSortParams';
// eslint-disable-next-line import/extensions
import { DeviceOwnerColumnOrderSortKeyMap, DeviceOwnerQueryParamKeyMap, DeviceOwnerStatus } from './enum/DeviceOwnerKeyMaps.enum';
import { DeviceOwnersPopover } from './DeviceOwnersPopover';
import DeviceOwnersTableHead from './DeviceOwnersTableHead';
import DeviceOwnersTableToolbar from './DeviceOwnersTableToolbar';
import DeviceOwnersDateRangeSelect from './DeviceOwnersDateRangeSelect';
import { DeviceOwnerAppManagementModal } from './DeviceOwnerAppManagementModal';
import { DeviceOwnerUninstallAppModal } from './DeviceOwnerUninstallAppModal';
import { DeviceOwnerInviteUnenrolledModal } from './DeviceOwnerInviteUnenrolledModal';
import { useQAsyncTablePagination } from '../shared/useQAsyncTablePagination';
import { getFilterValue } from '../../utils/filterHelpers';

interface QueryParams {
  [key: string]: string;
}
interface IProps {
  ownerStatus: (typeof DeviceOwnerStatus)[keyof typeof DeviceOwnerStatus];
}

export const useDeviceOwners = (
  queryParams: QueryParams,
  debouncedSearchQuery: string,
  selectedEnrolledDateRange: string,
  selectedLastCheckInDateRange: string,
  selectedInvitedDateRange: string,
  selectedUnenrolledDateRange: string,
  accessToken: string,
  ownerStatus: string,
) => {
  const queryString = createQueryString(queryParams);
  /* 
    NOTE: additional query filter parameters require special syntax incompatible with URLSearchParams
    each filter in query must be separated by a space / ' ' (%20) using tuples separated by ':' (%3A)
  */
  let filterParamString = `&q=`;
  if (!selectedEnrolledDateRange) {
    if (ownerStatus === DeviceOwnerStatus.ENROLLED) {
      filterParamString += encodeURIComponent('inviteAcceptedAt:not:null unenrolledAt:is:null');
    }
    if (ownerStatus === DeviceOwnerStatus.PENDING) {
      // default pending device owners order to inviteSentAt DESC
      if (!queryParams.sort) {
        filterParamString = '&sort=-inviteSentAt&q=';
      }
      filterParamString += encodeURIComponent('inviteAcceptedAt:is:null unenrolledAt:is:null');
    }
    if (ownerStatus === DeviceOwnerStatus.UNENROLLED) {
      filterParamString += encodeURIComponent('unenrolledAt:not:null');
    }
  } else {
    filterParamString += encodeURIComponent(DeviceOwnerQueryParamKeyMap.inviteAcceptedAt + selectedEnrolledDateRange);
  }

  if (debouncedSearchQuery) {
    filterParamString += encodeURIComponent(` ${DeviceOwnerQueryParamKeyMap.nameOrEmail + debouncedSearchQuery}`);
  }

  if (selectedLastCheckInDateRange) {
    filterParamString += encodeURIComponent(` ${DeviceOwnerQueryParamKeyMap.lastCheckinAt + selectedLastCheckInDateRange}`);
  }

  if (selectedInvitedDateRange) {
    filterParamString += encodeURIComponent(` ${DeviceOwnerQueryParamKeyMap.inviteSentAt + selectedInvitedDateRange}`);
  }

  if (selectedUnenrolledDateRange) {
    filterParamString += encodeURIComponent(` ${DeviceOwnerQueryParamKeyMap.unenrolledAt + selectedUnenrolledDateRange}`);
  }

  const queryParamString = queryString + filterParamString;

  const {
    data: deviceOwnersData,
    error: deviceOwnersError,
    mutate,
  } = useFetchService(`/organizations/deviceowners/search?${queryParamString}`, accessToken);

  const isLoading = !deviceOwnersData && !deviceOwnersError;

  const { deviceOwners, page: totals } = useStickyResult(deviceOwnersData);

  return { deviceOwners, deviceOwnersError, totals, isLoading, mutate };
};

export default function DeviceOwnersTable({ ownerStatus }: IProps) {
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const [selectedEnrolledDateRange, setSelectedEnrolledDateRange] = useState('all');
  const [selectedLastCheckInDateRange, setSelectedLastCheckInDateRange] = useState('all');
  const [selectedInvitedDateRange, setSelectedInvitedDateRange] = useState('all');
  const [selectedUnenrolledDateRange, setSelectedUnenrolledDateRange] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [popoverId, setPopoverId] = useState(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [selected, setSelected] = useState([]);
  const [currentManagedAppsOwner, setCurrentManagedAppsOwner] = useState(null);
  const [currentManagedApp, setCurrentManagedApp] = useState(null);
  const [editInfo, setEditInfo] = useState({ id: null, firstName: null, lastName: null, index: null });
  const [showDeviceOwnerAppManagementModal, setShowDeviceOwnerAppManagementModal] = useState(false);
  const [showDeviceOwnerUninstallAppModal, setShowDeviceOwnerUninstallAppModal] = useState(false);
  const [showDeviceOwnerInviteUnenrolledModal, setShowDeviceOwnerInviteUnenrolledModal] = useState(false);
  const [selectedInviteUnenrolledModalDeviceOwners, setSelectedInviteUnenrolledModalDeviceOwners] = useState([]);

  const { enqueueSnackbar } = useSnackbar();

  const { page, pageSize, pageSizeOptions, paginationOptions, setPaginationOptions, handleChangePage, handlePageSizeChange } =
    useQAsyncTablePagination();

  // splits strings on one or more space(s) and limits the search query to the first two terms provided
  const sanitizedSearchQueryTerms = String(searchQuery).toLowerCase().trim().split(/\s+/, 2);
  const searchTerms = sanitizedSearchQueryTerms
    .map((str, i) => {
      if (sanitizedSearchQueryTerms.length === 2 && i === 0) {
        return `${str}++`;
      }
      return str;
    })
    .join('');

  const query = searchQuery.length > 0 ? searchTerms : '';

  const [debouncedSearchQuery] = useDebounce(query, 500);

  const {
    deviceOwnerFirstNameOrder,
    toggleDeviceOwnerFirstNameOrder,
    deviceOwnerLastNameOrder,
    toggleDeviceOwnerLastNameOrder,
    deviceOwnerEmailOrder,
    toggleDeviceOwnerEmailOrder,
    deviceOwnerDateEnrolledOrder,
    toggleDeviceOwnerDateEnrolledOrder,
    deviceOwnerLastDeviceCheckInOrder,
    toggleDeviceOwnerLastDeviceCheckInOrder,
    deviceOwnerNumberOfDevicesOrder,
    toggleDeviceOwnerNumberOfDevicesOrder,
    deviceOwnerDateInvitedOrder,
    toggleDeviceOwnerDateInvitedOrder,
    deviceOwnerDateUnenrolledOrder,
    toggleDeviceOwnerDateUnenrolledOrder,
    resetToggles,
  } = useDeviceOwnerSortParams();

  const findSortParameter = (key: string, direction: null | 'ASC' | 'DESC') => {
    return direction === 'ASC'
      ? DeviceOwnerColumnOrderSortKeyMap[key]
      : direction === 'DESC'
      ? `-${DeviceOwnerColumnOrderSortKeyMap[key]}`
      : null;
  };

  const sort = [];
  sort.push(
    findSortParameter('firstName', deviceOwnerFirstNameOrder),
    findSortParameter('lastName', deviceOwnerLastNameOrder),
    findSortParameter('email', deviceOwnerEmailOrder),
    findSortParameter('dateEnrolled', deviceOwnerDateEnrolledOrder),
    findSortParameter('lastDeviceCheckIn', deviceOwnerLastDeviceCheckInOrder),
    findSortParameter('numberOfDevices', deviceOwnerNumberOfDevicesOrder),
    findSortParameter('dateInvited', deviceOwnerDateInvitedOrder),
    findSortParameter('dateUnenrolled', deviceOwnerDateUnenrolledOrder),
  );

  const enrolledDateRange = getFilterValue(selectedEnrolledDateRange);
  const lastCheckInDateRange = getFilterValue(selectedLastCheckInDateRange);
  const invitedDateRange = getFilterValue(selectedInvitedDateRange);
  const unenrolledDateRange = getFilterValue(selectedUnenrolledDateRange);

  const {
    deviceOwners,
    deviceOwnersError,
    totals = {},
    isLoading,
    mutate,
  } = useDeviceOwners(
    {
      page: String(page - 1), // API expects 0-based page index, QAsyncTablePagination expects 1-based page index
      size: String(pageSize),
      sort: sort.filter(order => order)[0],
    },
    debouncedSearchQuery,
    enrolledDateRange,
    lastCheckInDateRange,
    invitedDateRange,
    unenrolledDateRange,
    accessToken,
    ownerStatus,
  );

  const { totalItems: total, totalPages: totalPageCount } = totals;

  // Updates pagination options on load or when pageSize changes
  useEffect(() => {
    setPaginationOptions({
      total,
      lastPage: Math.ceil(total / pageSize),
      perPage: pageSize,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceOwners, pageSize]);

  const tableHeaders = {
    enrolled: [
      {
        title: 'First Name',
        onClick: toggleDeviceOwnerFirstNameOrder,
        sortValue: deviceOwnerFirstNameOrder,
      },
      {
        title: 'Last Name',
        onClick: toggleDeviceOwnerLastNameOrder,
        sortValue: deviceOwnerLastNameOrder,
      },
      {
        title: 'Email',
        onClick: toggleDeviceOwnerEmailOrder,
        sortValue: deviceOwnerEmailOrder,
      },
      {
        title: 'Date Enrolled',
        onClick: toggleDeviceOwnerDateEnrolledOrder,
        sortValue: deviceOwnerDateEnrolledOrder,
      },
      {
        title: 'Last Device Check-In',
        onClick: toggleDeviceOwnerLastDeviceCheckInOrder,
        sortValue: deviceOwnerLastDeviceCheckInOrder,
      },
      {
        title: '# of Devices',
        onClick: toggleDeviceOwnerNumberOfDevicesOrder,
        sortValue: deviceOwnerNumberOfDevicesOrder,
      },
      {
        // edit enrolled device owner column
        title: '',
        onClick: () => {},
        sortValue: null,
      },
    ],
    pending: [
      {
        title: 'First Name',
        onClick: toggleDeviceOwnerFirstNameOrder,
        sortValue: deviceOwnerFirstNameOrder,
      },
      {
        title: 'Last Name',
        onClick: toggleDeviceOwnerLastNameOrder,
        sortValue: deviceOwnerLastNameOrder,
      },
      {
        title: 'Email',
        onClick: toggleDeviceOwnerEmailOrder,
        sortValue: deviceOwnerEmailOrder,
      },
      {
        title: 'Date Invited',
        onClick: toggleDeviceOwnerDateInvitedOrder,
        sortValue: deviceOwnerDateInvitedOrder,
      },
      {
        // edit pending device owner column
        title: '',
        onClick: () => {},
        sortValue: null,
      },
    ],
    unenrolled: [
      {
        title: 'First Name',
        onClick: toggleDeviceOwnerFirstNameOrder,
        sortValue: deviceOwnerFirstNameOrder,
      },
      {
        title: 'Last Name',
        onClick: toggleDeviceOwnerLastNameOrder,
        sortValue: deviceOwnerLastNameOrder,
      },
      {
        title: 'Email',
        onClick: toggleDeviceOwnerEmailOrder,
        sortValue: deviceOwnerEmailOrder,
      },
      {
        title: 'Date Unenrolled',
        onClick: toggleDeviceOwnerDateUnenrolledOrder,
        sortValue: deviceOwnerDateUnenrolledOrder,
      },
      {
        // edit unrolled device owner column (invite)
        title: '',
        onClick: () => {},
        sortValue: null,
      },
    ],
  };

  const selectDropDownInfo = {
    enrolled: [
      {
        title: 'Date Enrolled',
        label: 'date-enrolled',
        value: selectedEnrolledDateRange,
      },
      {
        title: 'Last Device Check-In',
        label: 'last-device-check-in',
        value: selectedLastCheckInDateRange,
      },
    ],
    pending: [
      {
        title: 'Date Invited',
        label: 'date-invited',
        value: selectedInvitedDateRange,
      },
    ],
    unenrolled: [
      {
        title: 'Date Unenrolled',
        label: 'date-unenrolled',
        value: selectedUnenrolledDateRange,
      },
    ],
  };

  const selectDropDownCategory =
    ownerStatus === DeviceOwnerStatus.ENROLLED
      ? selectDropDownInfo.enrolled
      : ownerStatus === DeviceOwnerStatus.PENDING
      ? selectDropDownInfo.pending
      : selectDropDownInfo.unenrolled;

  const handleDateRangeChange = (event: ChangeEvent<HTMLInputElement>, category: string) => {
    if (ownerStatus === DeviceOwnerStatus.ENROLLED && category === 'date-enrolled') {
      setSelectedEnrolledDateRange(event.target.value);
    } else if (ownerStatus === DeviceOwnerStatus.ENROLLED && category === 'last-device-check-in') {
      setSelectedLastCheckInDateRange(event.target.value);
    } else if (ownerStatus === DeviceOwnerStatus.PENDING && category === 'date-invited') {
      setSelectedInvitedDateRange(event.target.value);
    } else if (ownerStatus === DeviceOwnerStatus.UNENROLLED && category === 'date-unenrolled') {
      setSelectedUnenrolledDateRange(event.target.value);
    }
  };

  const resetFilterAndSorts = () => {
    resetToggles();
    // handleChangePage(0); // replace?
    setSearchQuery('');
    setSelectedEnrolledDateRange('all');
    setSelectedLastCheckInDateRange('all');
    setSelectedInvitedDateRange('all');
    setSelectedUnenrolledDateRange('all');
  };

  // popover - pending tab - more button
  const handlePopoverClick = (event: MouseEvent<HTMLButtonElement>, deviceOwnerId: string) => {
    setPopoverId(deviceOwnerId);
    setPopoverAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setPopoverId(null);
    setPopoverAnchorEl(null);
  };

  // checkboxes
  const isSelected = (id: string) => {
    const index = selected.findIndex(owner => owner.id === id);
    return index !== -1;
  };

  const selectDeviceOwner = (id: string, email: string) => {
    const selectedIndex = selected.findIndex(owner => owner.id === id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, { id, email });
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const selectAllDeviceOwners = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelectedPolicies = deviceOwners.map(n => ({ id: n.id, email: n.email }));
      setSelected(newSelectedPolicies);
      return;
    }
    setSelected([]);
  };

  // editable cells
  const editRow = (id: string) => {
    deviceOwners.forEach((owner, index) => {
      if (owner.id === id) {
        setEditInfo({ ...editInfo, id, index });
      }
    });
  };

  const updateDeviceOwner = async () => {
    const { id, firstName, lastName } = editInfo;

    if (!firstName && !lastName) {
      closeEditableRow();
      return;
    }

    await sendHttpRequest({
      path: `${process.env.ORGANIZATION_SERVICE_URL}/organizations/deviceowners/${id}`,
      method: 'PUT',
      body: { firstName, lastName },
      successMessage: 'Successfully updated device owner',
      errorMessage: 'Error updating device owner',
      enqueueSnackbar,
    });

    closeEditableRow();
    mutate();
  };

  const closeEditableRow = () => {
    setEditInfo({ id: null, firstName: null, lastName: null, index: null });
  };

  // workaround to focus first name input when editing - autoFocus prop does not work
  // https://github.com/mui/material-ui/issues/1594
  // eslint-disable-next-line consistent-return
  const firstNameInput = useCallback(inputElement => {
    if (inputElement) {
      const timeout = setTimeout(() => {
        inputElement.focus();
      }, 100);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, []);

  if (deviceOwnersError) {
    console.error(deviceOwnersError);
    return <div>failed to load</div>;
  }

  if (!deviceOwners) return <PageLoader />;

  return (
    <>
      {isLoading && <PageLoader />}
      <DeviceOwnerAppManagementModal
        isOpen={showDeviceOwnerAppManagementModal}
        onClose={() => setShowDeviceOwnerAppManagementModal(false)}
        currentManagedAppsOwner={currentManagedAppsOwner}
        setShowDeviceOwnerUninstallModal={setShowDeviceOwnerUninstallAppModal}
        setCurrentManagedApp={setCurrentManagedApp}
      />
      <DeviceOwnerUninstallAppModal
        isOpen={showDeviceOwnerUninstallAppModal}
        onClose={() => setShowDeviceOwnerUninstallAppModal(false)}
        currentManagedAppsOwner={currentManagedAppsOwner}
        currentManagedApp={currentManagedApp}
      />
      <DeviceOwnerInviteUnenrolledModal
        isOpen={showDeviceOwnerInviteUnenrolledModal}
        onClose={() => setShowDeviceOwnerInviteUnenrolledModal(false)}
        selected={selectedInviteUnenrolledModalDeviceOwners}
        setSelected={setSelectedInviteUnenrolledModalDeviceOwners}
        mutate={mutate}
      />
      <StyledFilterWrapper>
        {selectDropDownCategory.map(({ title, label, value }) => {
          return (
            <DeviceOwnersDateRangeSelect
              key={`${label}-date-range-select-drop-down`}
              title={title}
              label={label}
              stateValue={value}
              handleDateRangeChange={handleDateRangeChange}
            />
          );
        })}
        <ResetButton
          onClick={() => {
            resetFilterAndSorts();
          }}
        />
      </StyledFilterWrapper>
      <StyledTableWrapper>
        <Box sx={{ backgroundColor: '#eeeeee', padding: '4px', borderRadius: '4px 4px 0 0' }}>
          <QSearchBar
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search device owners"
            sx={{ '.MuiOutlinedInput-root': { border: 'none' } }}
          />
        </Box>
        {paginationOptions.total > pageSizeOptions[0].value && (
          <QAsyncTablePagination
            pagination={{
              lastPage: totalPageCount,
              totalData: total,
              dataPerPage: pageSize,
            }}
            getPage={handleChangePage}
            page={page}
            showRowsPerPage
            currentRows={deviceOwners.length}
            handlePageSizeChange={handlePageSizeChange}
            pageSize={pageSize}
            pageSizeOptions={pageSizeOptions}
          />
        )}
        <DeviceOwnersTableToolbar
          numSelected={selected.length}
          selected={selected}
          onClickSelectAll={selectAllDeviceOwners}
          setSelected={setSelected}
          closeEditableRow={closeEditableRow}
          mutate={mutate}
          ownerStatus={ownerStatus}
        />
        <QTable aria-label="Device-Owners-Table">
          <DeviceOwnersTableHead
            ownerStatus={ownerStatus}
            onSelectAllClick={selectAllDeviceOwners}
            numSelected={selected.length}
            rowCount={deviceOwners.length}
            tableHeaders={tableHeaders}
            setPage={handleChangePage}
          />
          <QTableBody>
            {deviceOwners.map((owner, index) => {
              const isOwnerSelected = isSelected(owner.id);
              const labelId = `device-owners-table-checkbox-${index}`;
              return (
                <QTableRow
                  role="row"
                  key={owner.id}
                  selected={isOwnerSelected}
                  aria-label={`${owner.email} row`}
                  aria-checked={isOwnerSelected}
                >
                  {ownerStatus !== DeviceOwnerStatus.ENROLLED && (
                    <QTableCell
                      onClick={() => selectDeviceOwner(owner.id, owner.email)}
                      tabIndex={-1}
                      key={owner.id}
                      selected={isOwnerSelected}
                    >
                      <Checkbox
                        checked={isOwnerSelected}
                        inputProps={{
                          'aria-labelledby': labelId,
                          'aria-label': `${owner.email} checkbox`,
                          'aria-checked': isOwnerSelected,
                        }}
                        checkedIcon={<CheckBoxOutlinedIcon />}
                      />
                    </QTableCell>
                  )}
                  {/* shared columns */}
                  {editInfo.index === index && editInfo.id === owner.id ? (
                    <>
                      <QTableCell>
                        <StyledInput
                          defaultValue={owner.firstName}
                          placeholder="First Name"
                          onChange={e => setEditInfo({ ...editInfo, firstName: e.currentTarget.value })}
                          inputRef={firstNameInput}
                        />
                      </QTableCell>
                      <QTableCell>
                        <EditableRowWrapper>
                          <StyledInput
                            defaultValue={owner.lastName}
                            placeholder="Last Name"
                            onChange={e => setEditInfo({ ...editInfo, lastName: e.currentTarget.value })}
                          />
                          <IconButton onClick={updateDeviceOwner}>
                            <StyledDoneIcon />
                          </IconButton>
                          <IconButton onClick={closeEditableRow}>
                            <StyledCloseIcon />
                          </IconButton>
                        </EditableRowWrapper>
                      </QTableCell>
                    </>
                  ) : (
                    <>
                      <QTableCell>{owner.firstName}</QTableCell>
                      <QTableCell>{owner.lastName}</QTableCell>
                    </>
                  )}
                  <QTableCell>{owner.email}</QTableCell>
                  {ownerStatus === DeviceOwnerStatus.ENROLLED ? (
                    <>
                      <QTableCell>{owner.inviteAcceptedAt.length > 0 ? dayjs(owner.inviteAcceptedAt).format('M/DD/YYYY') : ''}</QTableCell>
                      <QTableCell>{calculateElapsedTime(owner.lastCheckinAt)}</QTableCell>
                      <QTableCell>{owner.deviceCount}</QTableCell>
                    </>
                  ) : ownerStatus === DeviceOwnerStatus.PENDING ? (
                    <>
                      <QTableCell>{calculateElapsedTime(owner.inviteSentAt)}</QTableCell>
                    </>
                  ) : (
                    <>
                      <QTableCell>{calculateElapsedTime(owner.unenrolledAt)}</QTableCell>
                    </>
                  )}
                  <StyledQTableCell>
                    {ownerStatus !== DeviceOwnerStatus.UNENROLLED ? (
                      <IconButton onClick={e => handlePopoverClick(e, owner.id)}>
                        <StyledMoreVertIcon />
                      </IconButton>
                    ) : (
                      <StyledInviteButton
                        variant="filled"
                        onClick={() => {
                          setShowDeviceOwnerInviteUnenrolledModal(true);
                          setSelectedInviteUnenrolledModalDeviceOwners([owner]);
                        }}
                      >
                        Invite
                      </StyledInviteButton>
                    )}
                  </StyledQTableCell>
                  <DeviceOwnersPopover
                    ownerStatus={ownerStatus}
                    deviceOwner={owner}
                    deviceOwnerId={popoverId}
                    anchorEl={popoverAnchorEl}
                    handleClose={handlePopoverClose}
                    mutate={mutate}
                    selected={selected}
                    setSelected={setSelected}
                    editRow={() => editRow(owner.id)}
                    setShowDeviceOwnerAppManagementModal={setShowDeviceOwnerAppManagementModal}
                    setCurrentManagedAppsOwner={setCurrentManagedAppsOwner}
                  />
                </QTableRow>
              );
            })}
          </QTableBody>
        </QTable>
        {paginationOptions.total > pageSizeOptions[0].value && (
          <QAsyncTablePagination
            pagination={{
              lastPage: totalPageCount,
              totalData: total,
              dataPerPage: pageSize,
            }}
            getPage={handleChangePage}
            page={page}
            showRowsPerPage
            currentRows={deviceOwners.length}
            handlePageSizeChange={handlePageSizeChange}
            pageSize={pageSize}
            pageSizeOptions={pageSizeOptions}
          />
        )}
      </StyledTableWrapper>
    </>
  );
}

const StyledFilterWrapper = styled('div')`
  display: flex;
  column-gap: 16px;
  margin-bottom: 30px;
  margin-top: 30px;
  align-items: center;
`;

const StyledTableWrapper = styled('div')`
  width: 100%;
`;

const iconStyles = props => css`
  color: ${props.theme.palette.accent.primary};
`;

const StyledMoreVertIcon = styled(MoreVertIcon)`
  ${iconStyles}
`;

const StyledDoneIcon = styled(DoneIcon)`
  ${iconStyles}
`;

const StyledCloseIcon = styled(CloseIcon)`
  ${iconStyles}
`;

const StyledQTableCell = styled(QTableCell)<{ selected?: boolean }>`
  text-align: right;
`;

const StyledInput = styled(InputBase)`
  background-color: rgba(255, 255, 255, 1);
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 4px;
  font-size: 0.875rem;
  padding: 0 5px;
  margin-right: 5px;
  &.Mui-focused {
    border: 1px solid ${({ theme }) => theme.palette.primary.main};
  }
`;

const StyledInviteButton = styled(QButton)`
  height: 32px;
  box-shadow: none;
  margin-left: auto;
  float: right;
  &:focus {
    background-color: ${props => props.theme.palette.accent.primary};
  }
`;

const EditableRowWrapper = styled('div')`
  display: flex;
  column-gap: 8px;
  align-items: center;
`;
